import React, {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {createPortal} from 'react-dom';
import PropTypes from 'prop-types';
import {GET_ORDER_SUCCESS as GET_ORDER_SUCCESS_LIST} from 'store/ordersV2/orderList/action';
import {
  EVENT_ORDER_TYPE_LIST,
  ORDER,
  ORDER_STATUS
} from 'helpers/constants/orders_v2';
import OrdersTable from 'routes/App/ordersV2/components/OrdersTable';
import {
  CHANGE_STATUS_ACTIVE,
  RUN_GET_ACTIVE_ORDERS_JOB,
  SET_ORDERS_SORTING,
  STOP_GET_ACTIVE_ORDERS_JOB,
  SET_ORDERS_SEARCH,
  SET_ACTIVE_EVENT_ORDERS
} from 'store/global/ordersV2/action';
import {
  CHANGE_STATUS,
  CLEAR_ORDER,
  GET_ORDER_SUCCESS
} from 'store/ordersV2/orderDetails/action';
import {OrdersContainer} from 'routes/App/ordersV2/OrdersList/styles';
import {OPEN_CANCELLATION_CONFIRMATION_MODAL_V2} from 'store/global/modals/action';
import {FlexContainer, FlexItem} from 'components/layout';
import SearchField from 'components/fields/SearchField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {nanoid} from 'nanoid';
import FormControl from '@material-ui/core/FormControl';
import {FEATURE_RESTRICTION_IDS} from 'helpers/constants/restrictions';
import {
  DashboardField,
  DashboardFieldInner,
  DashboardFieldLabel,
  DashboardFields,
  FieldLabel
} from '../../dashboard/styles';

const ActiveOrders = ({parentRef, print}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isNetworkOnline = useSelector(
    (state) => state.globalNotificationsReducer.isNetworkOnline
  );
  const {sort, sortBy} = useSelector(
    (state) => state.activeOrdersV2Reducer.sorting
  );
  const {
    activeOrders,
    search,
    filteredOrders,
    eventOrderTypeSelected
  } = useSelector((state) => state.activeOrdersV2Reducer);
  const loading = useSelector((state) => state.activeOrdersV2Reducer.loading);
  const changeStatusLoading = useSelector(
    (state) => state.activeOrdersV2Reducer.changeStatusLoading
  );
  const {Timezone, AllowedServiceFeatures, PickupReadyEnabled} = useSelector(
    (state) => state.user?.activeLocation
  );
  const showOtterBrandName = AllowedServiceFeatures?.includes(
    FEATURE_RESTRICTION_IDS.OTTER_SHOW_BRAND_NAME
  );

  useEffect(() => {
    dispatch(STOP_GET_ACTIVE_ORDERS_JOB());
    dispatch(RUN_GET_ACTIVE_ORDERS_JOB());
  }, []);

  const onStatusChange = (
    id,
    status,
    previousStatus,
    deliveryProvider,
    source
  ) => {
    if (status !== previousStatus && !changeStatusLoading) {
      if (status === ORDER_STATUS.CANCELLED) {
        dispatch(
          OPEN_CANCELLATION_CONFIRMATION_MODAL_V2({
            isOpen: true,
            info: {id, status, previousStatus, source},
            source: 'active',
            deliveryProvider
          })
        );
      } else {
        dispatch(CHANGE_STATUS_ACTIVE({id, status, previousStatus}));
        dispatch(STOP_GET_ACTIVE_ORDERS_JOB());
        dispatch(RUN_GET_ACTIVE_ORDERS_JOB());
      }
    }
  };
  const onPickupReady = (id, status, previousStatus) => {
    if (status !== previousStatus && !changeStatusLoading) {
      dispatch(CHANGE_STATUS_ACTIVE({id, status, previousStatus}));
      dispatch(STOP_GET_ACTIVE_ORDERS_JOB());
      dispatch(RUN_GET_ACTIVE_ORDERS_JOB());
    }
  };

  const openDetails = (data) => {
    // dispatch(GET_ORDER_SUCCESS({Timezone, ...data}));
    dispatch(CLEAR_ORDER());
    history.push(`/orders-v2/active/${data[ORDER.ID]}/details`, {
      windowPosition: window.scrollY
    });
  };

  const printOrder = (data) => {
    dispatch(GET_ORDER_SUCCESS_LIST(data));
    setTimeout(() => print(), 500);
  };

  const handleRequestSort = (property) => {
    const isAsc = sortBy === property.sortBy && sort === 'asc';
    dispatch(
      SET_ORDERS_SORTING({
        sort: isAsc ? 'desc' : 'asc',
        sortBy: property.sortBy
      })
    );
  };

  function descendingComparator(a, b, descendingOrderBy) {
    if (b[descendingOrderBy] < a[descendingOrderBy]) {
      return -1;
    }
    if (b[descendingOrderBy] > a[descendingOrderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(comparatorOrder, comparatorOrderBy) {
    return comparatorOrder === 'desc'
      ? (a, b) => descendingComparator(a, b, comparatorOrderBy)
      : (a, b) => -descendingComparator(a, b, comparatorOrderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const sortOrder = comparator(a[0], b[0]);
      if (sortOrder !== 0) return sortOrder;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const createSortHandler = (property) => {
    handleRequestSort(property);
  };

  const DashboardFilters = ({searchValue, onChangeValue}) => (
    <>
      <br />
      <DashboardFields className='AlignCenter spaceBetween'>
        <DashboardField className='wAutoSpace justifyEnd dFlex'>
          <FlexItem flex='0.3 1 auto'>
            <SearchField
              placeholder='Search orders by customer name'
              value={searchValue}
              onChange={(e) => {
                onChangeValue(SET_ORDERS_SEARCH(e.target.value));
              }}
              onClear={() => onChangeValue(SET_ORDERS_SEARCH(''))}
              isClearIconVisible={searchValue?.length > 0}
            />
          </FlexItem>
        </DashboardField>
      </DashboardFields>
    </>
  );

  DashboardFilters.propTypes = {
    searchValue: PropTypes.string.isRequired,
    onChangeValue: PropTypes.func.isRequired
  };

  const renderDashboardFilters = useCallback(
    (searchValue, onChangeValue) => {
      if (parentRef) {
        return createPortal(
          <DashboardFilters
            searchValue={searchValue}
            onChangeValue={onChangeValue}
          />,
          parentRef
        );
      }
      return null;
    },
    [parentRef, AllowedServiceFeatures]
  );

  return (
    <OrdersContainer className={showOtterBrandName ? 'withOtterBrandName' : ''}>
      {renderDashboardFilters(search, dispatch)}
      <OrdersTable
        onStatusChange={onStatusChange}
        openDetails={openDetails}
        printOrder={printOrder}
        orders={stableSort(
          search ? filteredOrders || [] : activeOrders || [],
          getComparator(sort, sortBy === 'CookingEndTime.Local' && 'date')
        )}
        onSort={createSortHandler}
        orderBy={sortBy}
        orderDirection={sort}
        loading={loading}
        isNetworkOnline={isNetworkOnline}
        changeStatusLoading={changeStatusLoading}
        isOrdersTable
        pickupReadyEnabled={PickupReadyEnabled}
        onPickupReady={onPickupReady}
        allowedServiceFeatures={AllowedServiceFeatures}
        showOtterBrandName={showOtterBrandName}
      />
    </OrdersContainer>
  );
};

ActiveOrders.propTypes = {
  parentRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.any})
  ]).isRequired,
  print: PropTypes.func.isRequired
};

export default ActiveOrders;
